import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization, CURRENT_ORG_STORAGE_KEY } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';
import { Button } from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import AuthBrandPanel from './AuthBrandPanel';
import {
    acceptInvite,
    getInviteDetails,
    InviteDetails,
    getPendingInviteToken,
    persistInviteToken,
    clearInviteToken,
} from '../../../services/modules/organizationService';
import { notify } from '../../../utils/toast';

const ROLE_LABELS: Record<string, string> = {
    admin: 'Administrador',
    member: 'Membro',
    viewer: 'Visualizador',
};

const InviteShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted p-6 md:p-10">
        <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-background shadow-lg ring-1 ring-border flex">
            <div className="flex-1 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                <div className="mx-auto w-full max-w-md">{children}</div>
            </div>
            <AuthBrandPanel
                tagline="Colabore com sua equipe"
                taglineHighlight="no mesmo CRM."
                description="Aceite o convite e comece a gerenciar leads, conversas e funis com sua organização."
            />
        </div>
    </div>
);

/** Conteúdo autenticado — renderizar dentro de OrganizationProvider. */
export const InviteAcceptMember: React.FC = () => {
    const { user, exitInviteToApp } = useAuth();
    const { refreshOrganizations } = useOrganization();
    const { setCurrentPage } = useAppContext();
    const token = getPendingInviteToken();

    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [done, setDone] = useState(false);
    const [details, setDetails] = useState<InviteDetails | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [orgName, setOrgName] = useState('');

    const goToApp = useCallback(() => {
        exitInviteToApp();
        setCurrentPage('dashboard');
    }, [exitInviteToApp, setCurrentPage]);

    useEffect(() => {
        if (token) persistInviteToken(token);
        (async () => {
            if (!token) {
                const orgs = await refreshOrganizations(true);
                if (orgs.length > 0) {
                    goToApp();
                    return;
                }
                setError('Link de convite inválido ou ausente.');
                setLoading(false);
                return;
            }
            const invite = await getInviteDetails(token);
            if (!invite) {
                // Aceite já consumiu o token (owner vê membro) — entra no CRM em vez de prender.
                const orgs = await refreshOrganizations(true);
                if (orgs.length > 0) {
                    goToApp();
                    return;
                }
                setError('Convite não encontrado, expirado ou já utilizado.');
                setLoading(false);
                return;
            }
            setDetails(invite);
            setLoading(false);
        })();
    }, [token, refreshOrganizations, goToApp]);

    const handleAccept = useCallback(async () => {
        if (!token) return;
        setAccepting(true);
        setError(null);
        try {
            const result = await acceptInvite(token);
            if (!result?.success) throw new Error(result?.error || 'Não foi possível aceitar o convite.');
            clearInviteToken();
            localStorage.removeItem('adlitics_new_registration');
            const name = (result.organization_name as string) || details?.organization_name || 'Organização';
            const orgId = result.organization_id as string | undefined;
            setOrgName(name);
            setDone(true);
            notify.success(`Você entrou em ${name}!`);
            if (orgId) {
                localStorage.setItem(CURRENT_ORG_STORAGE_KEY, orgId);
                await refreshOrganizations(true);
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Erro ao aceitar convite.';
            setError(msg);
            notify.error(msg);
        } finally {
            setAccepting(false);
        }
    }, [token, details, refreshOrganizations]);

    if (loading) {
        return <InviteShell><div className="flex justify-center py-16"><Spinner size="lg" color="primary" /></div></InviteShell>;
    }

    if (done) {
        return (
            <InviteShell>
                <div className="flex flex-col gap-6 text-center">
                    <h1 className="text-2xl font-bold">Convite aceito!</h1>
                    <p className="text-sm text-muted-foreground">Você agora faz parte de <strong>{orgName}</strong>.</p>
                    <Button type="button" onClick={goToApp} className="w-full font-semibold">Acessar o CRM</Button>
                </div>
            </InviteShell>
        );
    }

    if (error && !details) {
        return (
            <InviteShell>
                <div className="flex flex-col gap-4 text-center">
                    <h1 className="text-xl font-bold">Convite inválido</h1>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Button type="button" onClick={goToApp} variant="outline" className="w-full">Ir para o CRM</Button>
                </div>
            </InviteShell>
        );
    }

    const roleLabel = details?.role ? ROLE_LABELS[details.role] || details.role : 'Membro';
    return (
        <InviteShell>
            <div className="flex flex-col gap-6">
                <div className="text-center">
                    <h1 className="text-2xl font-bold">Junte-se a {details?.organization_name}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Função: {roleLabel}</p>
                </div>
                {error && <p className="text-sm text-destructive text-center">{error}</p>}
                <p className="text-center text-sm text-muted-foreground">Logado como <strong>{user?.email}</strong></p>
                <Button type="button" onClick={handleAccept} disabled={accepting} className="w-full font-semibold">
                    {accepting ? 'Aceitando...' : 'Aceitar convite'}
                </Button>
            </div>
        </InviteShell>
    );
};

/** Visitante — sem OrganizationProvider. */
const InviteAcceptGuest: React.FC = () => {
    const { navigateToLogin, navigateToRegister } = useAuth();
    const token = getPendingInviteToken();
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState<InviteDetails | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token) persistInviteToken(token);
        (async () => {
            if (!token) {
                setError('Link de convite inválido ou ausente.');
                setLoading(false);
                return;
            }
            const invite = await getInviteDetails(token);
            if (!invite) setError('Convite não encontrado, expirado ou já utilizado.');
            else setDetails(invite);
            setLoading(false);
        })();
    }, [token]);

    if (loading) {
        return <InviteShell><div className="flex justify-center py-16"><Spinner size="lg" color="primary" /></div></InviteShell>;
    }

    if (error || !details) {
        return (
            <InviteShell>
                <div className="flex flex-col gap-4 text-center">
                    <h1 className="text-xl font-bold">Convite inválido</h1>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Button variant="outline" onClick={navigateToLogin} className="w-full">Ir para login</Button>
                </div>
            </InviteShell>
        );
    }

    return (
        <InviteShell>
            <div className="flex flex-col gap-6 text-center">
                <h1 className="text-2xl font-bold">Junte-se a {details.organization_name}</h1>
                <p className="text-sm text-muted-foreground">Entre ou crie uma conta para aceitar o convite.</p>
                <Button type="button" onClick={navigateToRegister} className="w-full font-semibold">
                    Ingressar nessa organização
                </Button>
                <Button type="button" variant="outline" onClick={navigateToLogin} className="w-full">
                    Já tenho conta
                </Button>
            </div>
        </InviteShell>
    );
};

const InviteAccept: React.FC = () => {
    const { isAuthenticated } = useAuth();
    return isAuthenticated ? <InviteAcceptMember /> : <InviteAcceptGuest />;
};

export default InviteAccept;
